import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import depositv1Service from "../services/deposit.service";
import localization from "../../../config/localization";
import {
  AvailableDepositBonusesResponse,
  DepositTypeListResponseList,
  UserMobileNumberDetailResponse,
} from "../models/response";
import { localStorageService } from "../../../services";
import { BLOB_IMAGE_URL } from "../../../config/appConfig";
import { BlobContainerConsts } from "../../../models/constants/blobContainerConsts";
import CreateDepositRequest from "./CreateDepositRequest";
import { Button, Card, CardBody } from "../../../components/ui";
import { DepositType } from "../models/constants";
import { DeviceType } from "../../../models/constants/deviceType";
import userDeviceStore from "../../../store/device.store";
import { Modal } from "../../../components/ui/modals";

interface DepositV1Props {
  depositFlow: string;
  deviceType?: (typeof DeviceType)[keyof typeof DeviceType];
}

export default function DepositV1({ depositFlow, deviceType }: DepositV1Props) {
  const navigate = useNavigate();
  const isWebDevice = (deviceType ?? DeviceType.Web) === DeviceType.Web;
  const [depositTypes, setDepositTypes] = useState<
    DepositTypeListResponseList[]
  >([]);
  const [selectedDepositType, setSelectedDepositType] =
    useState<DepositTypeListResponseList | null>(null);
  const [availableDepositBonuses, setAvailableDepositBonuses] =
    useState<AvailableDepositBonusesResponse | null>(null);
  const [userMobileDetail, setUserMobileDetail] =
    useState<UserMobileNumberDetailResponse | null>(null);
  const [showVerifyMobileModal, setShowVerifyMobileModal] =
    useState<boolean>(false);
  const [showPendingDepositWarningModal, setShowPendingDepositWarningModal] =
    useState<boolean>(false);

  const usernameRef = useRef<string | null>("User");
  const accessModeSource = userDeviceStore((state) => state.accessModeSource);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const { pendingOnlineDeposits, pendingOfflineDeposits } =
      await depositv1Service.getUserPendingDeposits();
    if (pendingOnlineDeposits >= 1 && pendingOfflineDeposits >= 1) {
      setShowPendingDepositWarningModal(true);
      return;
    }
    let summary = await depositv1Service.getUserDepositSummary();
    if (summary) {
      let types = await depositv1Service.getDepositTypes(summary);
      if (
        (pendingOnlineDeposits >= 1 || pendingOfflineDeposits >= 1) &&
        (!types || types.length === 0)
      ) {
        setShowPendingDepositWarningModal(true);
        return;
      }
      if (types) {
        setDepositTypes(types);
      }
      const response = await depositv1Service.getUserMobileDetail();
      setUserMobileDetail(response);
      if (!userMobileDetail?.ismobileverified) {
        usernameRef.current = localStorageService.getItem<string>("username");
      }
    }
  };

  const onDepositTypeClick = async (item: DepositTypeListResponseList) => {
    if (
      item.name?.toLowerCase() === DepositType.ABCCrypto &&
      !userMobileDetail?.ismobileverified
    ) {
      setShowVerifyMobileModal(true);
      return;
    }
    setSelectedDepositType(item);
    const depositbonuses = await depositv1Service.getAvailableDepositBonuses(accessModeSource.sourceType);
    setAvailableDepositBonuses(depositbonuses);
  };

  const handleVerifyProceed = () => {
    navigate("/profile");
  };

  const handlePendingDepositClose = () => {
    navigate("/deposithistory");
  };

  if (selectedDepositType) {
    const child = (
      <CreateDepositRequest
        selectedDepositType={selectedDepositType}
        availableDepositBonuses={availableDepositBonuses}
        userMobileDetail={userMobileDetail}
        onBack={() => setSelectedDepositType(null)}
        depositFlow={depositFlow}
        deviceType={deviceType}
      />
    );

    return isWebDevice ? (
      <Card>
        <CardBody showGradient>
          <div className="w-full max-w-4xl mx-auto">
            {child}
          </div>
        </CardBody>
      </Card>
    ) : (
      child
    );
  }

  const content = (
    <div className="p-4">
      <div className="w-full max-w-4xl mx-auto">
        <p className="text-[22px] font-semibold">
          {localization.choose_your_deposit_method}
        </p>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
          {depositTypes?.map((item) => (
            <div
              key={item.id}
              onClick={() => onDepositTypeClick(item)}
              className="relative rounded-xl border border-gray-700 bg-gradient-to-b from-[#0b0b0b] to-black p-4 text-white hover:border-gray-500 transition cursor-pointer"
            >
              {item.processingTimeDescription && (
                <div className="absolute top-0 right-0">
                  <div className="rounded-bl-xl rounded-tr-xl border border-gray-600 bg-[#1f1f1f] px-3 py-1.5 text-[10px] font-semibold text-gray-300">
                    {item.processingTimeDescription}
                  </div>
                </div>
              )}

              <div>
                <img
                  src={
                    item.logoUrl &&
                    `${BLOB_IMAGE_URL}${BlobContainerConsts.Banks}/${item.logoUrl}`
                  }
                  alt={item.name}
                  className={`mx-auto w-20 h-12 object-contain ${item.processingTimeDescription ? "mt-5" : "mt-2"
                    }`}
                />
              </div>

              <div className="mt-3 text-center text-xs text-gray-400">
                <span className="font-normal text-[#C7C7CC] text-[10px]">
                  Min{" "}
                </span>
                <span className="font-medium text-[#EAEAEA] text-[10px]">
                  {"\u20B9"}
                  {item.minimumAmount}
                </span>

                <span className="mx-2 font-normal">-</span>

                <span className="font-normal text-[#C7C7CC] text-[10px]">
                  Max{" "}
                </span>
                <span className="font-medium text-[#EAEAEA] text-[10px]">
                  {"\u20B9"}
                  {item.maximumAmount}
                </span>
              </div>

              {item.depositMethodType &&
                item.depositMethodType.toLowerCase() !== "qr" &&
                item.depositMethodType.toLowerCase() !== "offline" && (
                  <div className="mt-2 flex items-center justify-center gap-1">
                    <img
                      src={"/icons/ic-instant.svg"}
                      alt="Instant"
                      className="w-24 h-7 object-contain"
                    />
                  </div>
                )}
            </div>
          ))}
        </div>
      </div>

      {showVerifyMobileModal && (
        <Modal isOpen={showVerifyMobileModal} onClose={handleVerifyProceed}>
          <p className="mt-3 text-[18px] leading-7 text-gray-600">
            {localization.hi_lbl}{" "}
            <span className="font-bold text-black">{usernameRef.current}</span>
          </p>

          <p className="mt-2 px-4 text-[18px] font-semibold leading-7 text-gray-600">
            {localization.crypto_deposit_mobile_verify}
          </p>

          <Button
            onClick={handleVerifyProceed}
            className="mt-6 w-full rounded-lg bg-[#111111] py-3.5 font-semibold text-white hover:bg-[#EDEAEA] hover:text-black transition-all duration-200"
          >
            {localization.proceed.toUpperCase()}
          </Button>
        </Modal>
      )}

      {showPendingDepositWarningModal && (
        <Modal isOpen={showPendingDepositWarningModal} showImg={false} onClose={handlePendingDepositClose}>
          <p className="mt-2 px-4 text-[18px] font-semibold leading-7 text-gray-600">
            {localization.depositprocessingpara1}
          </p>

          <Button
            onClick={handlePendingDepositClose}
            className="mt-6 w-full rounded-lg bg-[#111111] py-3.5 font-semibold text-white hover:bg-[#EDEAEA] hover:text-black transition-all duration-200"
          >
            {localization.ok_btn}
          </Button>
        </Modal>
      )}
    </div>
  );

  return isWebDevice ? (
    <Card>
      <CardBody showGradient>
        <div className="w-full max-w-4xl mx-auto">
          {content}
        </div>
      </CardBody>
    </Card>
  ) : (
    content
  );
}
